<template>
  <div
    class="field field--text"
    :style="{ '--form-item-width': grid.Cols, '--form-item-height': grid.Rows, '--form-item-start': grid.Offset }"
  >
    <span class="field__label">
      <label :for="id">
        <slot></slot>
      </label>
      <button v-if="!disabled && !readonly && value !== null" @click.prevent="clear">Rensa</button>
    </span>
    <span style="display: flex;" :class="dateClass">
      <masked-input
        type="date"
        ref="input"
        placeholder="20xx-xx-xx"
        :disabled="disabled || readonly"
        v-model="cValue"
        :filter="maxDateLength"
        @keydown.native.enter.prevent="onEnter"
        @focus="onFocus"
        @blur="onBlur"
      />
      <input
        v-if="type === 'datetime'"
        type="time"
        ref="time"
        :value="pickedTime"
        :disabled="disabled || readonly"
        @input="updateTime($event.target.value)"
        @keydown.enter.prevent="onEnter"
        @focus="onFocus"
        @blur="onBlur"
      />
    </span>
    <div style="display: flex; flex-direction: column;">
      <small v-if="errorContent" style="color: red; font-style: italic">{{ errorContent }}</small>
    </div>
  </div>
</template>

<script>
import 'vue-datetime/dist/vue-datetime.css'
import maskedInput from '@/components/maskedInput.vue'
import { DateTime } from '@/util/luxon'

export default {
  name: 'field-datetime',
  props: {
    error: String,
    grid: Object,
    disabled: Boolean,
    id: String,
    name: String,
    placeholder: String,
    readonly: Boolean,
    required: Boolean,
    type: String,
    showtime: Boolean,
    value: String
  },
  data() {
    return {
      dateClass: 'valid'
    }
  },
  computed: {
    cValue: {
      get() {
        return this.pickedDate
      },
      set(value) {
        this.update(value)
      }
    },
    pickedDateTime() {
      return this.value !== null ? DateTime.fromProfileDate(this.value) : null
    },
    pickedDate() {
      return this.value !== null ? DateTime.fromProfileDate(this.value).toFormat('yyyy-MM-dd') : null
    },
    pickedTime() {
      return this.value !== null ? DateTime.fromProfileDate(this.value).toFormat('HH:mm:ss') : null
    },
    errorContent() {
      return this.error || (this.dateClass === 'invalid' ? 'Ogiltigt datumformat' : '')
    }
  },
  watch: {
    error() {
      this.dateClass = this.error ? 'invalid' : 'valid'
    }
  },
  methods: {
    getInput() {
      return this.$refs?.input?.$el.querySelector('input')
    },
    clear() {
      this.update(null)
      // Focus field
      this.$nextTick(() => {
        this.getInput().focus()
      })
    },
    emitInput(value) {
      this.$emit('input', value)
    },
    validateDate(value) {
      let newDate = value?.split('T')[0] || value // Remove time part if present
      if (newDate === null || newDate.length === 0) {
        return null
      }
      // if the 3rd character is a dash, prepend the first 2 characters of the current year
      if (newDate.substr(2, 1) === '-') {
        newDate = `${DateTime.now()
          .toFormat('yyyy')
          .substr(0, 2)}${newDate}`
      }
      if (newDate !== null && newDate.length !== 10) {
        return null
      }

      newDate = DateTime.fromProfileDate(newDate)
      if (this.type === 'datetime') {
        const newTime = this.pickedTime ?? '00:00:00'
        newDate = newDate.set({
          hour: newTime.substr(0, 2),
          minute: newTime.substr(3, 2),
          second: newTime.substr(6, 2)
        })
      }
      const isValid = newDate.isValid
      if (!isValid) {
        return null
      }
      return newDate
    },
    update(value) {
      if (value === null || value.length === 0) {
        this.emitInput(null)
        return
      }

      const newDate = this.validateDate(value)
      if (newDate === null) {
        this.dateClass = 'invalid' // Set date class to invalid
        this.emitInput(value) // Emit the value unchanged
        return
      }
      this.dateClass = 'valid' // Reset date class to valid
      this.emitInput(newDate.toISO())
    },
    updateTime(newTime) {
      if (this.value === null) {
        return
      }
      var newValue = DateTime.fromProfileDate(this.value)
      newValue = newValue.set({
        hour: newTime.substr(0, 2),
        minute: newTime.substr(3, 2),
        second: newTime.substr(6, 2)
      })
      this.emitInput(newValue.toISO())
    },
    onBlur() {
      if (this.value === null && this.required) {
        this.update(DateTime.now().toFormat('yyyy-MM-dd'))
      }
    },
    onEnter() {
      // If no date is selected, set it to today
      if (this.value === null && this.required) {
        this.update(DateTime.now().toFormat('yyyy-MM-dd'))
      }

      // Check if date is valid
      if (this.value && this.validateDate(this.value) === null) {
        this.dateClass = 'invalid'
        return
      }

      this.$emit('nextInput')
    },
    onFocus() {
      // Select all text in the input
      this.getInput().select()
    },
    maxDateLength(char, value) {
      // Only allow digits and dashes
      // If the value contains dashes, it should be a max of 10 characters
      // If the value does not contain dashes, it should be a max of 8 characters
      if (char.match(/[0-9-]/) && (char === '-' || value.indexOf('-') === -1 ? value.length < 8 : value.length < 10)) {
        return true
      }

      return false
    }
  },
  components: {
    maskedInput
  }
}
</script>

<style scoped>
button {
  color: white;
  background-color: #ff6c6c;
  border-radius: 6px;
  border: 0;
  margin-left: 1rem;
}
.invalid /deep/ input:first-child {
  border: 1px solid red;
}

.writing /deep/ input:first-child {
  border: 1px solid yellow;
}
</style>
